import { useContext, useState, useEffect } from 'react';
import moment from 'moment';
import { TextField, CircularProgress } from '@mui/material';
import { Button } from '@fashionnovateam/nova-ui-toolkit';
import { CompletedItemLeadTime } from '../types';
import TableCell from '../../ReusableComponents/Table/TableCell';
import styles from './CompletedItems.module.css';
import { FeatureFlagsContext } from '../../../Lib/Contexts/FeatureFlagsContext';
import { useAddItemLeadTimeComment } from '../../../Lib/Hooks/item-lead-time.hooks';
import { getLastComment } from '../utils/commentUtils';

interface Props {
  userId: string;
  itemLeadTime: CompletedItemLeadTime;
  perms: {
    canUpdate: boolean;
    canSubmit: boolean;
  };
  onRowClick?: (worksheetId: string) => void;
}

const selectClass = (ready: boolean) =>
  ready ? styles.readyItemColumnText : styles.itemColumnText;

function CompletedItemTableRow({
  userId,
  itemLeadTime,
  perms,
  onRowClick,
}: Props) {
  const { flags } = useContext(FeatureFlagsContext);
  const [comments, setComments] = useState(
    getLastComment(itemLeadTime.comments),
  );
  const { mutate: addComment, isLoading: isSubmitting } =
    useAddItemLeadTimeComment();

  useEffect(() => {
    setComments(getLastComment(itemLeadTime.comments));
  }, [itemLeadTime.comments, itemLeadTime.worksheet_id]);

  const handleCellClick = () => {
    onRowClick?.(itemLeadTime.worksheet_id);
  };

  const handleCommentsChange = (value: string) => {
    setComments(value);
  };

  const handleSubmit = () => {
    if (!perms.canUpdate) return;

    const commentValue = comments?.trim() || '';
    if (!commentValue) {
      return;
    }

    addComment({
      userId,
      data: {
        worksheet_id: itemLeadTime.worksheet_id,
        comments: commentValue,
      },
    });
  };

  const hasChanges = comments !== getLastComment(itemLeadTime.comments);

  const cellClass = `${styles.cell} cursor-pointer hover:bg-neutral-50`;

  return (
    <>
      <TableCell
        padding="normal"
        align="left"
        className={`${styles.stickyItemColumn} ${cellClass} font-medium`}
        onClick={handleCellClick}
      >
        {itemLeadTime.style_color}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.requested_date
          ? moment(itemLeadTime.requested_date).utc().format('MM/DD/YYYY')
          : '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.updated_at
          ? moment(itemLeadTime.updated_at).utc().format('MM/DD/YYYY')
          : '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        ${itemLeadTime.cost_jr}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        ${itemLeadTime.cost_plus}
      </TableCell>
      {flags?.baseline_cost?.enabled && (
        <>
          <TableCell className={cellClass} onClick={handleCellClick}>
            ${itemLeadTime.baseline_cost_jr || '-'}
          </TableCell>
          <TableCell className={cellClass} onClick={handleCellClick}>
            ${itemLeadTime.baseline_cost_plus || '-'}
          </TableCell>
        </>
      )}
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.country_of_origin || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.earliest_custom_cut_delivery_date
          ? moment(itemLeadTime.earliest_custom_cut_delivery_date)
              .utc()
              .format('MM/DD/YYYY')
          : '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.custom_cut_lead_time_in_weeks || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.shipment_mode || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.exclusivity || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.stock_quantity || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.stock_ratio || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.earliest_IHD_date_to_deliver_stock
          ? moment(itemLeadTime.earliest_IHD_date_to_deliver_stock)
              .utc()
              .format('MM/DD/YYYY')
          : '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.incoming_stock_quantity || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.incoming_stock_date
          ? moment(itemLeadTime.incoming_stock_date).utc().format('MM/DD/YYYY')
          : '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.incoming_stock_ratio || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.custom_MOQ_jr || '-'}
      </TableCell>
      <TableCell className={cellClass} onClick={handleCellClick}>
        {itemLeadTime.custom_MOQ_plus || '-'}
      </TableCell>
      <TableCell className={selectClass(true)}>
        <TextField
          id="outlined-comments"
          variant="outlined"
          className={styles.input}
          disabled={!perms.canUpdate || isSubmitting}
          value={comments}
          onChange={(e) => handleCommentsChange(e.target.value)}
          multiline
          rows={2}
          fullWidth
        />
      </TableCell>
      <TableCell className={selectClass(true)}>
        {perms.canUpdate && (
          <Button
            disabled={isSubmitting || !hasChanges || !comments?.trim()}
            onClick={handleSubmit}
            className="w-40 py-6"
          >
            {isSubmitting ? <CircularProgress /> : 'Save Comment'}
          </Button>
        )}
      </TableCell>
    </>
  );
}

export default CompletedItemTableRow;
